import { z } from 'zod';
import { defineRawRoute, defineRoute } from '../core/contract';
import { LAB_EVENT_REJECT_CODES } from '../core/errors';
import { eventBatchResZ, MAX_EVENT_BATCH } from '../core/event-batch';
import {
  AGENT_SCOPES,
  GAME_DEF_ORIGINS,
  GAME_DEF_STATUSES,
  LAB_BADGES,
  LAB_EQUIPMENT,
  LAB_ICON_KEYS,
  LAB_TAGS,
  MCP_BODY_LIMIT,
  OAUTH_BODY_LIMIT,
} from '../domain/gamelab/constants';
import { IdZ, OkZ, qInt, TsZ, textZ } from '../schemas/common';
import { LabEventZ, StoredLabEventZ } from '../schemas/event';
import { KeeperStateZ } from '../schemas/game';

/**
 * Owner: gamelab (docs/design/add-gamelab.md). REST contracts for the PWA. The MCP tool
 * shapes belong to the gamelab module and may be added to this file. Definitions travel
 * as `def: unknown` here; shared/src/domain/gamelab/validate.ts is the real check.
 */

export const GameDefStatusZ = z.enum(GAME_DEF_STATUSES);
export const GameDefOriginZ = z.enum(GAME_DEF_ORIGINS);
export const AgentScopeZ = z.enum(AGENT_SCOPES);
export const LabEquipmentZ = z.enum(LAB_EQUIPMENT);

export const ValidationIssueZ = z.object({
  /** JSON pointer into the definition: "/actions/2/effects/0/by". */
  path: z.string(),
  code: z.string(),
  message: z.string(),
  hint: z.string().optional(),
});
export const ValidationZ = z.object({
  ok: z.boolean(),
  errors: z.array(ValidationIssueZ),
  warnings: z.array(ValidationIssueZ),
});

export const GameDefSummaryZ = z.object({
  id: IdZ,
  slug: z.string().regex(/^[a-z0-9-]{3,40}$/),
  version: z.number().int().positive(),
  name: z.string(),
  tagline: z.string(),
  icon: z.enum(LAB_ICON_KEYS),
  status: GameDefStatusZ,
  origin: GameDefOriginZ,
  players: z.object({ min: z.number().int(), max: z.number().int() }),
  teamMode: z.enum(['solo', 'teams', 'either']),
  minutes: z.number().int(),
  needsHoop: z.boolean(),
  equipment: z.array(LabEquipmentZ),
  tags: z.array(z.enum(LAB_TAGS)),
  difficulty: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  pitch: z.string().nullable(),
  requestId: IdZ.nullable(),
  runs30d: z.number().int(),
  lastPlayedAt: TsZ.nullable(),
  createdAt: TsZ,
  updatedAt: TsZ,
  shelvedAt: TsZ.nullable(),
});
export type GameDefSummary = z.infer<typeof GameDefSummaryZ>;

export const GameFeedbackZ = z.object({
  id: IdZ,
  memberId: IdZ,
  kind: z.enum(['send_back', 'thumb_up', 'thumb_down']),
  text: z.string().nullable(),
  createdAt: TsZ,
});

export const GameDefDetailZ = GameDefSummaryZ.extend({
  /** The DSL v1 document (GameDef), validated by domain/gamelab/validate.ts. */
  def: z.unknown(),
  defHash: z.string(),
  validation: ValidationZ,
  simOk: z.boolean(),
  versions: z.array(z.object({ id: IdZ, version: z.number().int(), status: GameDefStatusZ, createdAt: TsZ })),
  feedback: z.array(GameFeedbackZ),
  stats: z.object({
    runs: z.number().int(),
    finals: z.number().int(),
    avgMinutes: z.number().nullable(),
    abandonRate: z.number().nullable(),
  }),
  request: z.object({ id: IdZ, text: z.string(), authorMemberId: IdZ }).nullable(),
});
export type GameDefDetail = z.infer<typeof GameDefDetailZ>;

export const GameRequestReplyZ = z.object({
  id: IdZ,
  fromKind: z.enum(['agent', 'member']),
  memberId: IdZ.nullable(),
  connectionId: IdZ.nullable(),
  text: z.string(),
  defId: IdZ.nullable(),
  createdAt: TsZ,
});

export const GameRequestZ = z.object({
  id: IdZ,
  authorMemberId: IdZ,
  text: z.string(),
  playersMin: z.number().int().nullable(),
  playersMax: z.number().int().nullable(),
  needsHoop: z.boolean().nullable(),
  equipment: z.array(LabEquipmentZ),
  status: z.enum(['open', 'answered', 'closed']),
  createdAt: TsZ,
  closedAt: TsZ.nullable(),
  replies: z.array(GameRequestReplyZ),
});
export type GameRequest = z.infer<typeof GameRequestZ>;

export const LabResultZ = z.object({
  winners: z.array(IdZ),
  places: z.record(z.string(), z.number().int()),
  points: z.record(z.string(), z.number().int()),
  stats: z.record(z.string(), z.record(z.string(), z.number().int())),
  funTag: z.string().optional(),
});
export type LabResult = z.infer<typeof LabResultZ>;

export const LabRunZ = z.object({
  id: IdZ,
  defId: IdZ,
  slug: z.string(),
  name: z.string(),
  sessionId: IdZ.nullable(),
  status: z.enum(['live', 'final', 'abandoned']),
  createdBy: IdZ,
  keeperMemberId: IdZ.nullable(),
  keeperDeviceId: IdZ.nullable(),
  keeperEpoch: z.number().int(),
  seed: z.number().int(),
  options: z.record(z.string(), z.union([z.number(), z.boolean(), z.string()])),
  players: z.array(z.object({ playerId: IdZ, slot: z.number().int(), team: z.string().nullable() })),
  startedAt: TsZ,
  endedAt: TsZ.nullable(),
  result: LabResultZ.nullable(),
  revision: z.number().int(),
});
export type LabRun = z.infer<typeof LabRunZ>;

export const LabSettingsZ = z.object({
  enabled: z.boolean(),
  shareNames: z.enum(['names', 'aliases']),
  court: z.object({
    hoops: z.number().int().min(0).max(8),
    balls: z.number().int().min(0).max(20),
    cones: z.number().int().min(0).max(50),
    notes: z.string().max(280),
  }),
});
export type LabSettings = z.infer<typeof LabSettingsZ>;

export const AgentConnectionZ = z.object({
  id: IdZ,
  name: z.string(),
  kind: z.enum(['static', 'oauth']),
  clientId: z.string().nullable(),
  scopes: z.array(AgentScopeZ),
  createdBy: IdZ,
  createdAt: TsZ,
  lastUsedAt: TsZ.nullable(),
  revokedAt: TsZ.nullable(),
  /** Display prefix of the static token ("ttsa_Ab12"), never the token. */
  tokenPrefix: z.string().nullable(),
});

export const LabEventBatchResZ = eventBatchResZ(LAB_EVENT_REJECT_CODES);

// Games (definitions)

export const listGames = defineRoute({
  name: 'gamelab.listGames',
  method: 'GET',
  path: '/api/gamelab/games',
  auth: 'mem',
  query: z.object({ status: GameDefStatusZ.optional(), origin: GameDefOriginZ.optional() }),
  res: z.object({ items: z.array(GameDefSummaryZ) }),
});

export const getGame = defineRoute({
  name: 'gamelab.getGame',
  method: 'GET',
  path: '/api/gamelab/games/:id',
  auth: 'mem',
  res: GameDefDetailZ,
  errors: ['NOT_FOUND'],
});

/**
 * The paste-a-draft path (ADDENDUM A15): a member pastes a game document into the PWA when the
 * agent cannot write. Same validation and simulation as create_draft. The document travels as
 * `def: unknown`, the server runs domain/gamelab/validate.ts on it.
 */
export const createDraft = defineRoute({
  name: 'gamelab.createDraft',
  method: 'POST',
  path: '/api/gamelab/games',
  auth: 'memw',
  body: z.object({ id: IdZ, def: z.unknown(), requestId: IdZ.optional() }),
  res: GameDefDetailZ,
  status: 201,
  idempotent: true,
  maxBodyBytes: 96 * 1024,
  errors: ['VALIDATION', 'CONFLICT'],
  summary: 'Store a pasted draft. Drafts with errors are stored, they just cannot be shelved.',
});

export const updateDraft = defineRoute({
  name: 'gamelab.updateDraft',
  method: 'PUT',
  path: '/api/gamelab/games/:id',
  auth: 'memw',
  body: z.object({ def: z.unknown() }),
  res: GameDefDetailZ,
  maxBodyBytes: 96 * 1024,
  errors: ['NOT_FOUND', 'CONFLICT', 'VALIDATION'],
  summary: 'Replace the document of a draft. Anything else is immutable (edit it by pasting a new draft).',
});

export const shelveGame = defineRoute({
  name: 'gamelab.shelveGame',
  method: 'POST',
  path: '/api/gamelab/games/:id/shelve',
  auth: 'memw',
  body: z.object({ defHash: z.string().max(200).optional() }),
  res: GameDefSummaryZ,
  errors: ['CONFLICT', 'VALIDATION'],
  summary:
    'Ready games, or drafts that validate and have a passing simulation (A7). The app sends the defHash of the version the member read; a different hash answers CONFLICT (details.code "changed").',
});

export const discardGame = defineRoute({
  name: 'gamelab.discardGame',
  method: 'POST',
  path: '/api/gamelab/games/:id/discard',
  auth: 'memw',
  res: GameDefSummaryZ,
});

export const retireGame = defineRoute({
  name: 'gamelab.retireGame',
  method: 'POST',
  path: '/api/gamelab/games/:id/retire',
  auth: 'own',
  res: GameDefSummaryZ,
});

export const feedback = defineRoute({
  name: 'gamelab.feedback',
  method: 'POST',
  path: '/api/gamelab/games/:id/feedback',
  auth: 'mem',
  body: z.object({ id: IdZ, kind: GameFeedbackZ.shape.kind, text: textZ(1, 500).optional() }),
  res: OkZ,
  idempotent: true,
});

// Requests

export const listRequests = defineRoute({
  name: 'gamelab.listRequests',
  method: 'GET',
  path: '/api/gamelab/requests',
  auth: 'mem',
  query: z.object({ status: z.enum(['open', 'answered', 'closed', 'all']).optional() }),
  res: z.object({ items: z.array(GameRequestZ) }),
});

export const createRequest = defineRoute({
  name: 'gamelab.createRequest',
  method: 'POST',
  path: '/api/gamelab/requests',
  auth: 'mem',
  body: z.object({
    id: IdZ,
    text: textZ(1, 280),
    playersMin: z.number().int().min(1).max(16).optional(),
    playersMax: z.number().int().min(1).max(16).optional(),
    needsHoop: z.boolean().optional(),
    equipment: z.array(LabEquipmentZ).max(6).optional(),
  }),
  res: GameRequestZ,
  status: 201,
  idempotent: true,
  errors: ['RATE_LIMITED'],
  summary: '5 requests a day per member.',
});

export const getRequest = defineRoute({
  name: 'gamelab.getRequest',
  method: 'GET',
  path: '/api/gamelab/requests/:id',
  auth: 'mem',
  res: GameRequestZ,
  errors: ['NOT_FOUND'],
});

export const replyRequest = defineRoute({
  name: 'gamelab.replyRequest',
  method: 'POST',
  path: '/api/gamelab/requests/:id/replies',
  auth: 'mem',
  body: z.object({ id: IdZ, text: textZ(1, 500) }),
  res: GameRequestZ,
  idempotent: true,
});

export const closeRequest = defineRoute({
  name: 'gamelab.closeRequest',
  method: 'POST',
  path: '/api/gamelab/requests/:id/close',
  auth: 'mem',
  res: GameRequestZ,
});

// Runs

export const listRuns = defineRoute({
  name: 'gamelab.listRuns',
  method: 'GET',
  path: '/api/gamelab/runs',
  auth: 'mem',
  query: z.object({
    status: z.enum(['live', 'final', 'abandoned']).optional(),
    defId: IdZ.optional(),
    sessionId: IdZ.optional(),
    before: qInt(0, Number.MAX_SAFE_INTEGER).optional(),
    limit: qInt(1, 100).optional(),
  }),
  res: z.object({ items: z.array(LabRunZ), nextBefore: TsZ.nullable() }),
});

export const getRun = defineRoute({
  name: 'gamelab.getRun',
  method: 'GET',
  path: '/api/gamelab/runs/:id',
  auth: 'mem',
  res: z.object({ run: LabRunZ, serverSeq: z.number().int().nonnegative() }),
  errors: ['NOT_FOUND'],
});

export const postRunEvents = defineRoute({
  name: 'gamelab.postRunEvents',
  method: 'POST',
  path: '/api/gamelab/runs/:id/events',
  auth: 'sk',
  body: z.object({ events: z.array(LabEventZ).min(1).max(MAX_EVENT_BATCH) }),
  res: LabEventBatchResZ,
  maxBodyBytes: 256 * 1024,
  summary: 'R4 shape. The start event creates the run, so a run can begin offline.',
});

export const pullRunEvents = defineRoute({
  name: 'gamelab.pullRunEvents',
  method: 'GET',
  path: '/api/gamelab/runs/:id/events',
  auth: 'mem',
  query: z.object({ after: qInt(0, Number.MAX_SAFE_INTEGER).optional() }),
  res: z.object({ events: z.array(StoredLabEventZ), serverSeq: z.number().int(), serverNow: TsZ }),
});

export const runKeeper = defineRoute({
  name: 'gamelab.runKeeper',
  method: 'POST',
  path: '/api/gamelab/runs/:id/keeper',
  auth: 'mem',
  body: z.object({ action: z.enum(['take', 'give']), toMemberId: IdZ.optional() }),
  res: KeeperStateZ,
});

export const LiveLabMessageZ = z.discriminatedUnion('event', [
  z.object({
    event: z.literal('lab.snapshot'),
    data: z.object({ defId: IdZ, events: z.array(StoredLabEventZ), serverSeq: z.number().int() }),
  }),
  z.object({ event: z.literal('lab.event'), data: z.object({ event: StoredLabEventZ }) }),
  z.object({
    event: z.literal('lab.status'),
    data: z.object({ status: z.enum(['live', 'final', 'abandoned']), keeperMemberId: IdZ.nullable() }),
  }),
  z.object({ event: z.literal('lab.final'), data: z.object({ result: LabResultZ }) }),
  z.object({ event: z.literal('bye'), data: z.object({}) }),
]);

export const liveRun = defineRoute({
  name: 'gamelab.liveRun',
  method: 'GET',
  path: '/api/live/lab/:id',
  auth: 'mem',
  kind: 'sse',
  res: LiveLabMessageZ,
  summary: 'SSE through the generic core/sse.ts, 15 s ping, Last-Event-ID = serverSeq.',
});

// Me and head-to-head

export const labMe = defineRoute({
  name: 'gamelab.me',
  method: 'GET',
  path: '/api/gamelab/me',
  auth: 'mem',
  query: z.object({ playerId: IdZ.optional() }),
  res: z.object({
    playerId: IdZ,
    funXp: z.object({ total: z.number().int(), today: z.number().int(), dailyCap: z.number().int() }),
    badges: z.array(z.object({ badge: z.enum(LAB_BADGES), unlockedAt: TsZ })),
    recent: z.array(
      z.object({
        runId: IdZ,
        name: z.string(),
        place: z.number().int().nullable(),
        points: z.number().int().nullable(),
        won: z.boolean(),
        funXp: z.number().int(),
        endedAt: TsZ,
      }),
    ),
  }),
});

export const labH2H = defineRoute({
  name: 'gamelab.h2h',
  method: 'GET',
  path: '/api/gamelab/h2h',
  auth: 'mem',
  query: z.object({ a: IdZ, b: IdZ }),
  res: z.object({
    a: IdZ,
    b: IdZ,
    aWins: z.number().int(),
    bWins: z.number().int(),
    runs: z.number().int(),
    last: z.array(z.object({ runId: IdZ, name: z.string(), winner: IdZ.nullable(), endedAt: TsZ })),
  }),
});

// Owner admin: agent connections and Lab settings

export const agentConnections = defineRoute({
  name: 'gamelab.agentConnections',
  method: 'GET',
  path: '/api/gamelab/agent/connections',
  auth: 'own',
  res: z.object({ items: z.array(AgentConnectionZ) }),
});

export const mintToken = defineRoute({
  name: 'gamelab.mintToken',
  method: 'POST',
  path: '/api/gamelab/agent/tokens',
  auth: 'own',
  body: z.object({
    name: textZ(1, 40),
    scopes: z.array(AgentScopeZ).min(1),
    expiresInDays: z.union([z.literal(30), z.literal(90), z.literal(365)]).default(90),
  }),
  res: z.object({ connection: AgentConnectionZ, token: z.string(), expiresAt: TsZ }),
  status: 201,
  errors: ['MCP_DISABLED'],
  summary: 'The token (ttsa_...) is shown once and stored only as an HMAC.',
});

export const connectCode = defineRoute({
  name: 'gamelab.connectCode',
  method: 'POST',
  path: '/api/gamelab/agent/connect-codes',
  auth: 'own',
  body: z.object({ scopes: z.array(AgentScopeZ).min(1) }),
  res: z.object({ code: z.string(), expiresAt: TsZ }),
  status: 201,
  errors: ['MCP_DISABLED'],
  summary: '8-char single-use code for the OAuth authorize page, 10 minutes.',
});

export const revokeConnection = defineRoute({
  name: 'gamelab.revoke',
  method: 'POST',
  path: '/api/gamelab/agent/connections/:id/revoke',
  auth: 'own',
  res: AgentConnectionZ,
});

export const agentAudit = defineRoute({
  name: 'gamelab.agentAudit',
  method: 'GET',
  path: '/api/gamelab/agent/audit',
  auth: 'own',
  query: z.object({ before: qInt(0, Number.MAX_SAFE_INTEGER).optional(), limit: qInt(1, 200).optional() }),
  res: z.object({
    items: z.array(
      z.object({
        id: z.number().int(),
        connectionId: IdZ.nullable(),
        tool: z.string(),
        bytes: z.number().int().nullable(),
        ok: z.boolean(),
        errorCode: z.string().nullable(),
        ms: z.number().int().nullable(),
        at: TsZ,
      }),
    ),
    nextBefore: z.number().int().nullable(),
  }),
});

export const labSettingsGet = defineRoute({
  name: 'gamelab.settingsGet',
  method: 'GET',
  path: '/api/gamelab/settings',
  auth: 'mem',
  res: LabSettingsZ,
});

export const labSettingsPut = defineRoute({
  name: 'gamelab.settingsPut',
  method: 'PUT',
  path: '/api/gamelab/settings',
  auth: 'own',
  body: LabSettingsZ,
  res: LabSettingsZ,
});

// Non-/api routes bound by gamelab through ServerModule.rawRoutes (A9 items 1 and 2).

export const mcpPost = defineRawRoute({
  name: 'gamelab.mcp',
  method: 'POST',
  path: '/mcp',
  auth: 'bearer',
  owner: 'gamelab',
  maxBodyBytes: MCP_BODY_LIMIT,
  summary: 'Stateless Streamable HTTP, JSON responses. 401 with WWW-Authenticate when no token.',
});
export const mcpGet = defineRawRoute({
  name: 'gamelab.mcpGet',
  method: 'GET',
  path: '/mcp',
  auth: 'pub',
  owner: 'gamelab',
  summary: '405',
});
export const protectedResourceMeta = defineRawRoute({
  name: 'gamelab.oauthProtectedResource',
  method: 'GET',
  path: '/.well-known/oauth-protected-resource',
  auth: 'pub',
  owner: 'gamelab',
});
export const protectedResourceMetaMcp = defineRawRoute({
  name: 'gamelab.oauthProtectedResourceMcp',
  method: 'GET',
  path: '/.well-known/oauth-protected-resource/mcp',
  auth: 'pub',
  owner: 'gamelab',
});
export const authServerMeta = defineRawRoute({
  name: 'gamelab.oauthAuthorizationServer',
  method: 'GET',
  path: '/.well-known/oauth-authorization-server',
  auth: 'pub',
  owner: 'gamelab',
});
export const oauthAuthorizeGet = defineRawRoute({
  name: 'gamelab.oauthAuthorizeGet',
  method: 'GET',
  path: '/oauth/authorize',
  auth: 'pub',
  owner: 'gamelab',
});
export const oauthAuthorizePost = defineRawRoute({
  name: 'gamelab.oauthAuthorizePost',
  method: 'POST',
  path: '/oauth/authorize',
  auth: 'pub',
  owner: 'gamelab',
  maxBodyBytes: OAUTH_BODY_LIMIT,
});
export const oauthToken = defineRawRoute({
  name: 'gamelab.oauthToken',
  method: 'POST',
  path: '/oauth/token',
  auth: 'pub',
  owner: 'gamelab',
  maxBodyBytes: OAUTH_BODY_LIMIT,
});
export const oauthRegister = defineRawRoute({
  name: 'gamelab.oauthRegister',
  method: 'POST',
  path: '/oauth/register',
  auth: 'pub',
  owner: 'gamelab',
  maxBodyBytes: OAUTH_BODY_LIMIT,
});
